/**
 * Access Control Manager
 * Enterprise-grade access control system for MARIA
 * Implements hierarchical RBAC with fine-grained permissions
 */

import { EventEmitter } from "node:events";

export interface User {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  permissions?: PermissionSet;
  organizationId: string;
  teamIds?: string[];
  departmentId?: string;
}

export interface Role {
  id: string;
  name: string;
  level: "individual" | "team" | "department" | "organization";
  permissions: PermissionSet;
  priority: number;
  inheritFromParent: boolean;
  overrideChild: boolean;
}

export interface PermissionSet {
  resources: Record<string, ResourcePermission>;
  actions: string[];
  conditions?: PermissionCondition[];
}

export interface ResourcePermission {
  read: boolean;
  write: boolean;
  delete: boolean;
  execute: boolean;
  share?: boolean;
  admin?: boolean;
}

export interface PermissionCondition {
  type: "time" | "location" | "resource" | "custom";
  operator: "equals" | "contains" | "matches" | "between";
  value: any;
}

export interface HierarchyLevel {
  level: string;
  priority: number;
  inheritFromParent: boolean;
  overrideChild: boolean;
}

export interface DataClassification {
  levels: string[];
  defaultLevel: string;
  rules: ClassificationRule[];
}

export interface ClassificationRule {
  pattern: string | RegExp;
  level: string;
  tags?: string[];
}

export interface AccessControlConfig {
  organizationId: string;
  hierarchyLevels: HierarchyLevel[];
  defaultPermissions: PermissionSet;
  dataClassification: DataClassification;
  auditEnabled: boolean;
}

export interface AccessDecision {
  allowed: boolean;
  reason?: string;
  appliedRoles?: Role[];
  conditions?: PermissionCondition[];
}

export class AccessControlManager extends EventEmitter {
  private config: AccessControlConfig;
  private users: Map<string, User> = new Map();
  private roles: Map<string, Role> = new Map();
  private sessions: Map<string, SessionInfo> = new Map();
  private cache: Map<string, CachedDecision> = new Map();

  constructor(config: AccessControlConfig) {
    super();
    this.config = config;
    this.initialize();
  }

  private initialize(): void {
    // Initialize default roles based on hierarchy levels
    this.config.hierarchyLevels.forEach((level) => {
      const defaultRole: Role = {
        id: `default-${level.level}`,
        name: `Default ${level.level} Role`,
        level: level.level as any,
        permissions: this.config.defaultPermissions,
        priority: level.priority,
        inheritFromParent: level.inheritFromParent,
        overrideChild: level.overrideChild,
      };
      this.roles.set(defaultRole.id, defaultRole);
    });

    this.emit("initialized", { organizationId: this.config.organizationId });
  }

  /**
   * Check if a user has access to a resource
   */
  async checkAccess(
    userId: string,
    resource: string,
    action: string,
    context?: Record<string, any>
  ): Promise<AccessDecision> {
    const cacheKey = `${userId}:${resource}:${action}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.decision;
    }

    const user = this.users.get(userId);
    if (!user) {
      return { allowed: false, reason: "User not found" };
    }

    // Collect all applicable permissions
    const applicablePermissions = this.collectPermissions(user);
    
    // Evaluate permissions
    const decision = this.evaluatePermissions(
      applicablePermissions,
      resource,
      action,
      context
    );

    // Cache the decision
    this.cache.set(cacheKey, {
      decision,
      expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
    });

    // Emit audit event
    if (this.config.auditEnabled) {
      this.emit("access-check", {
        userId,
        resource,
        action,
        decision,
        timestamp: new Date(),
      });
    }

    return decision;
  }

  /**
   * Grant a role to a user
   */
  async grantRole(userId: string, roleId: string): Promise<void> {
    const user = this.users.get(userId);
    const role = this.roles.get(roleId);

    if (!user || !role) {
      throw new Error("User or role not found");
    }

    if (!user.roles.find((r) => r.id === roleId)) {
      user.roles.push(role);
      this.clearUserCache(userId);

      this.emit("role-granted", {
        userId,
        roleId,
        timestamp: new Date(),
      });
    }
  }

  /**
   * Revoke a role from a user
   */
  async revokeRole(userId: string, roleId: string): Promise<void> {
    const user = this.users.get(userId);

    if (!user) {
      throw new Error("User not found");
    }

    const index = user.roles.findIndex((r) => r.id === roleId);
    if (index !== -1) {
      user.roles.splice(index, 1);
      this.clearUserCache(userId);

      this.emit("role-revoked", {
        userId,
        roleId,
        timestamp: new Date(),
      });
    }
  }

  /**
   * Register a new user
   */
  async registerUser(user: User): Promise<void> {
    this.users.set(user.id, user);
    
    // Assign default role based on organization level
    const defaultRole = this.roles.get("default-individual");
    if (defaultRole && !user.roles.find((r) => r.id === defaultRole.id)) {
      user.roles.push(defaultRole);
    }

    this.emit("user-registered", {
      userId: user.id,
      timestamp: new Date(),
    });
  }

  /**
   * Create a custom role
   */
  async createRole(role: Role): Promise<void> {
    this.roles.set(role.id, role);
    
    this.emit("role-created", {
      roleId: role.id,
      timestamp: new Date(),
    });
  }

  /**
   * Get classification level for data
   */
  getDataClassification(data: string): string {
    for (const rule of this.config.dataClassification.rules) {
      const pattern = rule.pattern instanceof RegExp 
        ? rule.pattern 
        : new RegExp(rule.pattern, "i");
      
      if (pattern.test(data)) {
        return rule.level;
      }
    }
    
    return this.config.dataClassification.defaultLevel;
  }

  /**
   * Collect all permissions for a user
   */
  private collectPermissions(user: User): PermissionSet[] {
    const permissions: PermissionSet[] = [];

    // Add user's direct permissions
    if (user.permissions) {
      permissions.push(user.permissions);
    }

    // Add role permissions (sorted by priority)
    const sortedRoles = [...user.roles].sort((a, b) => b.priority - a.priority);
    
    for (const role of sortedRoles) {
      permissions.push(role.permissions);
      
      // Handle inheritance
      if (role.inheritFromParent) {
        const parentRole = this.findParentRole(role);
        if (parentRole) {
          permissions.push(parentRole.permissions);
        }
      }
    }

    return permissions;
  }

  /**
   * Evaluate permissions against a resource and action
   */
  private evaluatePermissions(
    permissions: PermissionSet[],
    resource: string,
    action: string,
    context?: Record<string, any>
  ): AccessDecision {
    for (const permSet of permissions) {
      // Check if action is allowed
      if (!permSet.actions.includes(action) && !permSet.actions.includes("*")) {
        continue;
      }

      // Check resource permissions
      const resourcePerms = permSet.resources[resource] || permSet.resources["*"];
      if (!resourcePerms) {
        continue;
      }

      // Map action to permission
      const hasPermission = this.mapActionToPermission(action, resourcePerms);
      if (!hasPermission) {
        continue;
      }

      // Check conditions
      if (permSet.conditions) {
        const conditionsMet = this.evaluateConditions(permSet.conditions, context);
        if (!conditionsMet) {
          continue;
        }
      }

      return {
        allowed: true,
        conditions: permSet.conditions,
      };
    }

    return {
      allowed: false,
      reason: "No matching permissions found",
    };
  }

  /**
   * Map an action to resource permissions
   */
  private mapActionToPermission(action: string, perms: ResourcePermission): boolean {
    const actionMap: Record<string, keyof ResourcePermission> = {
      read: "read",
      view: "read",
      write: "write",
      create: "write",
      update: "write",
      delete: "delete",
      remove: "delete",
      execute: "execute",
      run: "execute",
      share: "share",
      admin: "admin",
      manage: "admin",
    };

    const permKey = actionMap[action.toLowerCase()];
    return permKey ? perms[permKey] === true : false;
  }

  /**
   * Evaluate permission conditions
   */
  private evaluateConditions(
    conditions: PermissionCondition[],
    context?: Record<string, any>
  ): boolean {
    if (!context) return false;

    for (const condition of conditions) {
      if (!this.evaluateCondition(condition, context)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Evaluate a single condition
   */
  private evaluateCondition(
    condition: PermissionCondition,
    context: Record<string, any>
  ): boolean {
    const value = context[condition.type];
    if (value === undefined) return false;

    switch (condition.operator) {
      case "equals":
        return value === condition.value;
      case "contains":
        return String(value).includes(String(condition.value));
      case "matches":
        return new RegExp(condition.value).test(String(value));
      case "between":
        return value >= condition.value[0] && value <= condition.value[1];
      default:
        return false;
    }
  }

  /**
   * Find parent role in hierarchy
   */
  private findParentRole(role: Role): Role | undefined {
    const currentLevel = this.config.hierarchyLevels.find(
      (l) => l.level === role.level
    );
    
    if (!currentLevel) return undefined;

    const parentLevel = this.config.hierarchyLevels.find(
      (l) => l.priority === currentLevel.priority + 1
    );

    if (!parentLevel) return undefined;

    return Array.from(this.roles.values()).find(
      (r) => r.level === parentLevel.level
    );
  }

  /**
   * Clear cache for a user
   */
  private clearUserCache(userId: string): void {
    const keysToDelete: string[] = [];
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${userId}:`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  /**
   * Get user by ID
   */
  getUser(userId: string): User | undefined {
    return this.users.get(userId);
  }

  /**
   * Get role by ID
   */
  getRole(roleId: string): Role | undefined {
    return this.roles.get(roleId);
  }

  /**
   * List all users
   */
  listUsers(): User[] {
    return Array.from(this.users.values());
  }

  /**
   * List all roles
   */
  listRoles(): Role[] {
    return Array.from(this.roles.values());
  }
}

// Helper interfaces
interface SessionInfo {
  userId: string;
  startedAt: Date;
  expiresAt: Date;
  permissions: PermissionSet;
}

interface CachedDecision {
  decision: AccessDecision;
  expiresAt: number;
}

// Export singleton instance
export const accessControlManager = new AccessControlManager({
  organizationId: "default",
  hierarchyLevels: [
    {
      level: "individual",
      priority: 1,
      inheritFromParent: false,
      overrideChild: true,
    },
    {
      level: "team",
      priority: 2,
      inheritFromParent: true,
      overrideChild: true,
    },
    {
      level: "department",
      priority: 3,
      inheritFromParent: true,
      overrideChild: true,
    },
    {
      level: "organization",
      priority: 4,
      inheritFromParent: false,
      overrideChild: false,
    },
  ],
  defaultPermissions: {
    resources: {
      "*": {
        read: true,
        write: false,
        delete: false,
        execute: false,
      },
    },
    actions: ["read", "view"],
  },
  dataClassification: {
    levels: ["public", "internal", "confidential", "restricted"],
    defaultLevel: "internal",
    rules: [],
  },
  auditEnabled: true,
});